import { useState, useEffect } from 'react';
import { useUserAlias } from './useUserAlias';

const FEEDBACK_STORAGE_KEY = 'webmec-feedback';
const FEEDBACK_PROBABILITY = 0.33; // 33% chance to show

interface FeedbackState {
  selected: boolean;
  submitted?: boolean;
  skipped?: boolean;
  dismissed?: number;
  expires: string;
}

const getStoredData = (key: string): FeedbackState | null => {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('Error reading localStorage:', error);
    return null;
  }
};

const setStoredData = (key: string, data: FeedbackState): void => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error('Error writing localStorage:', error);
  }
};

const createExpirationDate = (): string => {
  const date = new Date();
  date.setDate(date.getDate() + 30);
  return date.toISOString().split('T')[0]; // YYYY-MM-DD format
};

export const useFeedbackModal = () => {
  const { alias } = useUserAlias();
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    if (!alias) return;
    if (alias.includes('tod-adam')) return;

    const storageKey = `${FEEDBACK_STORAGE_KEY}-${alias}`;
    let shouldShowModal = false;

    const data = getStoredData(storageKey);

    if (data) {
      const hasExpired = new Date().toISOString().split('T')[0] > data.expires;

      if (hasExpired) {
        // Period expired, remove old data and check probability for new period
        localStorage.removeItem(storageKey);
        shouldShowModal = Math.random() < FEEDBACK_PROBABILITY;

        setStoredData(storageKey, {
          selected: shouldShowModal,
          expires: createExpirationDate(),
        });
      } else {
        // Current period is still valid
        shouldShowModal =
          data.selected &&
          !data.submitted &&
          !data.skipped &&
          (data.dismissed || 0) < 2;
      }
    } else {
      // First time user - check probability
      shouldShowModal = Math.random() < FEEDBACK_PROBABILITY;
      setStoredData(storageKey, {
        selected: shouldShowModal,
        expires: createExpirationDate(),
      });
    }

    setShouldShow(shouldShowModal);
  }, [alias]);

  const hideFeedback = () => {
    if (!alias) {
      setShouldShow(false);
      return;
    }

    const storageKey = `${FEEDBACK_STORAGE_KEY}-${alias}`;
    const data = getStoredData(storageKey);
    if (data) {
      setStoredData(storageKey, {
        ...data,
        dismissed: (data.dismissed || 0) + 1,
      });
    }

    setShouldShow(false);
  };

  const updateFeedbackState = (update: Partial<FeedbackState>) => {
    if (!alias) return;
    const storageKey = `${FEEDBACK_STORAGE_KEY}-${alias}`;

    const data = getStoredData(storageKey);
    if (data) {
      setStoredData(storageKey, { ...data, ...update });
    }

    setShouldShow(false);
  };

  const markAsSubmitted = () => updateFeedbackState({ submitted: true });

  const markAsSkipped = () => updateFeedbackState({ skipped: true });

  return { shouldShow, hideFeedback, markAsSubmitted, markAsSkipped };
};
